import Signer from '@aws-amplify/core/lib-esm/Signer';
import {Credentials} from 'aws-sdk';

let cachedBaseUrl = '';

async function getBaseUrl(): Promise<string> {
    if (cachedBaseUrl) return cachedBaseUrl;
    const res = await fetch(`${window.location.origin}/settings.json`);
    const settings = await res.json();
    cachedBaseUrl = settings.NA?.cassandraEndpoint ?? '';
    return cachedBaseUrl;
}

let userId = 'portal-user';

export function setUserId(id: string) {
    userId = id;
}

function signedFetch(url: string, body: string, credentials: Credentials, signal?: AbortSignal): Promise<Response> {
    const signed = Signer.sign(
        {url, method: 'POST', headers: {'Content-Type': 'application/json'}, body, data: body},
        {
            access_key: credentials.accessKeyId,
            secret_key: credentials.secretAccessKey,
            session_token: credentials.sessionToken,
        },
        {region: 'us-east-1', service: 'lambda'}
    );
    return fetch(url, {
        method: 'POST',
        headers: {...signed.headers, 'Amz-Mons-Idp-Subject': userId},
        body,
        signal,
    });
}

export interface Chat {
    id: string;
    title: string;
    createdAt: string;
    updatedAt: string;
}

export interface ChatMessage {
    id: string;
    role: 'user' | 'assistant';
    parts: Array<{type: string; text?: string}>;
    metadata?: {finishedAt?: number};
}

export async function listChats(credentials: Credentials): Promise<Chat[]> {
    const baseUrl = await getBaseUrl();
    const res = await signedFetch(`${baseUrl}/chat/list`, JSON.stringify({json: {}}), credentials);
    const data = await res.json();
    const chats = data.json?.chats ?? data.json?.sessions ?? data.json?.items ?? [];
    return chats.map((c: any) => ({...c, id: c.chatId ?? c.id}));
}

export async function createChat(title: string, credentials: Credentials): Promise<Chat> {
    const baseUrl = await getBaseUrl();
    const res = await signedFetch(`${baseUrl}/chat/create`, JSON.stringify({json: {title}}), credentials);
    const data = await res.json();
    const chat = data.json;
    return {...chat, id: chat.chatId ?? chat.id};
}

export async function deleteChat(chatId: string, credentials: Credentials): Promise<void> {
    const baseUrl = await getBaseUrl();
    await signedFetch(`${baseUrl}/chat/delete`, JSON.stringify({json: {chatId}}), credentials);
}

export async function getMessages(chatId: string, credentials: Credentials): Promise<ChatMessage[]> {
    const baseUrl = await getBaseUrl();
    const res = await signedFetch(`${baseUrl}/chat/getMessages`, JSON.stringify({json: {chatId}}), credentials);
    const data = await res.json();
    return data.json ?? [];
}

export type StreamEvent =
    | {type: 'reasoning-delta'; delta: string}
    | {type: 'reasoning-end'}
    | {type: 'tool-input-available'; toolName: string; input: Record<string, unknown>}
    | {type: 'tool-output-available'; toolCallId: string; output: unknown}
    | {type: 'tool-output-error'; toolCallId: string; errorText: string}
    | {type: 'text-delta'; delta: string}
    | {type: 'suggestions'; suggestions: string[]};

export function sendMessageStream(
    chatId: string,
    messages: ChatMessage[],
    credentials: Credentials,
    onDelta: (text: string) => void,
    onDone: () => void,
    onError: (err: string) => void,
    onEvent?: (event: StreamEvent) => void
): AbortController {
    const controller = new AbortController();

    getBaseUrl()
        .then((baseUrl) => {
            const url = `${baseUrl}/chat/sendMessage`;
            const body = JSON.stringify({json: {chatId, messages}});
            return signedFetch(url, body, credentials, controller.signal);
        })
        .then(async (res) => {
            console.log('sendMessage response status:', res.status);
            const reader = res.body?.getReader();
            if (!reader) return onError('No response stream');
            const decoder = new TextDecoder();
            let buffer = '';
            let reading = true;
            while (reading) {
                const {done, value} = await reader.read();
                if (done) {
                    reading = false;
                } else {
                    buffer += decoder.decode(value, {stream: true});
                    const lines = buffer.split('\n');
                    buffer = lines.pop() ?? '';
                    for (const line of lines) {
                        if (!line.startsWith('data: ')) continue;
                        try {
                            const parsed = JSON.parse(line.slice(6));
                            const msg = parsed.json ?? parsed;
                            if (msg?.type === 'text-delta') {
                                console.log('TEXT DELTA:', msg.delta);
                                onDelta(msg.delta);
                            }
                            if (onEvent) {
                                switch (msg?.type) {
                                    case 'reasoning-delta':
                                    case 'reasoning-end':
                                    case 'tool-input-available':
                                    case 'tool-output-available':
                                    case 'tool-output-error':
                                    case 'text-delta':
                                        onEvent(msg as StreamEvent);
                                        break;
                                    default:
                                        if (msg?.type === 'data-suggestions' && Array.isArray(msg?.data)) {
                                            onEvent({type: 'suggestions', suggestions: msg.data});
                                        }
                                        break;
                                }
                            }
                        } catch {
                            /* skip */
                        }
                    }
                }
            }
            onDone();
        })
        .catch((err) => onError(err.message));

    return controller;
}
